import { SmsConsentPrompt } from '@Auth/components/SmsConsentForm';
import { SmsDisclosure } from '@Auth/components/SmsDisclosure';
import { TwoFactorBackupCodes as BackupCodes } from '@Auth/components/TwoFactorBackupCodes';
import { formatResendWait, useResendCountdown } from '@Auth/components/useResendCountdown';
import { Button } from '@Pages/pageRuntime/components/ui/Button';
import { ErrorMessage } from '@Pages/pageRuntime/components/ui/ErrorMessage';
import { useTwoFactorSend, useTwoFactorTarget } from '@Pages/pageRuntime/hooks/twoFactorFlows';
import { cn } from '@Pages/pageRuntime/lib/utils';
import { usePageRuntime } from '@Pages/pageRuntime/pageRuntimeContextValue';
import { apiErrorMessage } from '@Pages/pageRuntime/sdk/apiErrors';
import type { SdkTwoFactorFlow } from '@Pages/pageRuntime/sdk/types';
import type React from 'react';
import { useState } from 'react';

type TwoFactorSmsConsentProps = {
  /** The flow whose code this consent sends: a sign-in challenge, an MFA setup, or the proof for new backup codes. */
  flow: SdkTwoFactorFlow;

  /** The authenticatable resource. Defaults as in `Sdk.hooks.useTwoFactorChallenge`. */
  resourceId?: string;

  /** Additional classes for the container. */
  className?: string;

  /** Additional classes for the submit button. */
  buttonClassName?: string;
};

/**
 * The SMS consent step of a two-factor flow: the number the code goes to, an unchecked
 * agreement checkbox, the SMS Terms and Privacy Policy links, and "Text me a code". The
 * wording is fixed, because the stored consent records the notice version that names it.
 * Use it in a custom UI when the hook's step is `consent`.
 *
 * @example
 * <Sdk.Components.TwoFactorSmsConsent flow="challenge" />
 */
export function TwoFactorSmsConsent({
  flow,
  resourceId,
  className,
  buttonClassName
}: TwoFactorSmsConsentProps) {
  const target = useTwoFactorTarget(resourceId);
  const sender = useTwoFactorSend(flow, target.resourceId);
  // An agreement holds only for the token it was given to: a new number or notice asks again.
  const [agreedToken, setAgreedToken] = useState<string | null>(null);
  const state = sender.state;

  if (state === null || state.notice === null || state.maskedPhone === null) {
    return null;
  }

  const notice = state.notice;

  return (
    <div className={className}>
      <SmsConsentPrompt
        maskedPhone={state.maskedPhone}
        notice={notice}
        agreed={agreedToken === notice.consent_token}
        onAgreedChange={(agreed) => setAgreedToken(agreed ? notice.consent_token : null)}
        onSubmit={() =>
          void sender.send({
            sms_consent: true,
            notice_version: notice.version,
            consent_token: notice.consent_token
          })
        }
        processing={sender.isPending}
        errors={sender.error ? [apiErrorMessage(sender.error, 'The code was not sent.')] : []}
        buttonClassName={buttonClassName}
      />
    </div>
  );
}

type TwoFactorSendCodeButtonProps = TwoFactorSmsConsentProps & {
  /** Contents of the button. */
  label?: React.ReactNode;

  /** The button variant. */
  variant?: 'default' | 'outline' | 'link';
};

/**
 * Texts a new code for a two-factor flow. The SMS Terms and Privacy Policy links always
 * appear under it. A sign-in challenge counts down until the next code is allowed. When
 * the number has no stored consent yet, it shows `Sdk.Components.TwoFactorSmsConsent`
 * instead.
 *
 * @example
 * <Sdk.Components.TwoFactorSendCodeButton flow="challenge" label="Send a new code" />
 */
export function TwoFactorSendCodeButton({
  flow,
  resourceId,
  label = 'Text me a code',
  variant = 'outline',
  className,
  buttonClassName
}: TwoFactorSendCodeButtonProps) {
  const target = useTwoFactorTarget(resourceId);
  const sender = useTwoFactorSend(flow, target.resourceId);
  const state = sender.state;
  const wait = useResendCountdown(state?.resendAvailableIn ?? 0, state?.response);

  if (state === null || state.notice === null) {
    return null;
  }

  if (state.consentRequired) {
    return (
      <TwoFactorSmsConsent
        flow={flow}
        resourceId={resourceId}
        className={className}
        buttonClassName={buttonClassName}
      />
    );
  }

  return (
    <div className={cn('grid gap-2', className)}>
      {wait > 0 ? (
        <p className="text-sm text-muted-foreground">
          You can request a new code in {formatResendWait(wait)}
        </p>
      ) : (
        <Button
          type="button"
          variant={variant}
          className={buttonClassName}
          disabled={sender.isPending}
          onClick={() => void sender.send(null)}
        >
          {label}
        </Button>
      )}
      <ErrorMessage
        errors={sender.error ? [apiErrorMessage(sender.error, 'The code was not sent.')] : []}
      />
      <SmsDisclosure notice={state.notice} />
    </div>
  );
}

type TwoFactorBackupCodeListProps = {
  /** The plain backup codes from `codes` of a two-factor hook. */
  codes: string[];

  /** Shown under the codes, such as the button that continues. */
  children?: React.ReactNode;

  /** Additional classes for the container. */
  className?: string;
};

/**
 * Shows new backup codes once, with Copy and Download buttons. Session replay never
 * records the list. The codes are not stored anywhere on the page; do not store them.
 *
 * @example
 * {setup.codes ? <Sdk.Components.TwoFactorBackupCodeList codes={setup.codes} /> : null}
 */
export function TwoFactorBackupCodeList({
  codes,
  children,
  className
}: TwoFactorBackupCodeListProps) {
  const workspaceName = usePageRuntime()?.Sdk.workspace?.name ?? 'this workspace';

  return (
    <div className={className}>
      <BackupCodes codes={codes} workspaceName={workspaceName}>
        {children}
      </BackupCodes>
    </div>
  );
}
